/**
 * «Finalizar jornada» (BHM-CLD-REQ-045a): resumen real del día y, si hay trabajo activo, decisión obligatoria sobre la tarea actual.
 * Compartido por «Mi trabajo» y «Mi jornada»:
 *   <FinishDayButton onChanged={() => recargar()} />
 * Si el resumen no se puede cargar, el propio diálogo muestra el error.
 * `onChanged` se llama tras cerrar la jornada (ya se ha avisado al contador y al flujo con `notifyActivityChanged`).
 */
import { useState } from 'react';
import { activityApi, errText, fmtHm, type CloseAction, type CloseDayResult, type WorkdayWrap } from '../../stores/activityApi';
import { Modal, Icon, ErrorBanner, btnGhost, btnPrimary } from './WorkUi';
import { notifyActivityChanged } from './WorkdayCounter';
import { CLOSE_ACTIONS_UI, activeLegend, canConfirmClose, closeActionDesc, exceptionsLine, exitTimeOf, fmtClockTime, isCloseActionDisabled } from './flowLogic';

function Summary({ wrap }: { wrap: WorkdayWrap }) {
  const ex = exceptionsLine(wrap.exceptions);
  return (
    <>
      {wrap.workday.staleOpen && (
        <p className="rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-sm text-[#F59E0B]">
          Tu jornada seguía abierta desde un día anterior. Al finalizar se registra la hora actual como salida; BarmetHub no inventa una hora distinta.
        </p>
      )}
      <section className="grid gap-3 sm:grid-cols-3" aria-label="Resumen del día">
        <div className="rounded-xl border border-outline-variant px-3 py-2">
          <p className="text-[11px] uppercase tracking-wider text-on-surface-variant">Tiempo registrado</p>
          <p className="text-xl font-semibold tabular-nums text-on-surface" data-testid="wrap-registered">{fmtHm(wrap.registeredSec)}</p>
        </div>
        <div className="rounded-xl border border-outline-variant px-3 py-2">
          <p className="text-[11px] uppercase tracking-wider text-on-surface-variant">Tareas completadas hoy</p>
          <p className="text-xl font-semibold text-on-surface" data-testid="wrap-completed">{wrap.completed.length}</p>
        </div>
        <div className="rounded-xl border border-outline-variant px-3 py-2">
          <p className="text-[11px] uppercase tracking-wider text-on-surface-variant">Tareas abiertas</p>
          <p className="text-xl font-semibold text-on-surface">{wrap.pendingTasks}</p>
        </div>
      </section>
      {wrap.completed.length > 0 && (
        <ul className="space-y-1 text-sm text-on-surface-variant">{wrap.completed.map((t) => <li key={t.id}>✓ {t.title} · {t.projectName}</li>)}</ul>
      )}
      {wrap.blocked.length > 0 && <p className="text-sm text-[#F43F5E]">Bloqueadas: {wrap.blocked.map((b) => b.title).join(' · ')}</p>}
      {ex && <p className="text-sm text-[#F59E0B]">{ex}</p>}
      <p className="text-sm text-on-surface-variant" data-testid="wrap-next">
        {wrap.next
          ? <>Siguiente prevista: <b className="text-on-surface">{wrap.next.task.title} · {wrap.next.project.name}</b>.</>
          : 'Siguiente prevista: no hay una tarea lista para mañana.'}{' '}
        <span className="text-outline">(BarmetHub aún no tiene planificación con hora.)</span>
      </p>
    </>
  );
}

function Decision({ wrap, choice, onChoice }: { wrap: WorkdayWrap; choice: CloseAction | null; onChoice: (a: CloseAction) => void }) {
  const { active } = wrap;
  return (
    <fieldset className="space-y-2" data-testid="finish-day-decision">
      <legend className="mb-1 text-sm font-semibold text-on-surface">{activeLegend(active.tasks)}</legend>
      {active.nexusWaiting > 0 && <p className="text-xs text-[#60a5fa]">Nexus espera una respuesta tuya en {active.nexusWaiting} ejecución(es).</p>}
      {active.nexusRunning > 0 && <p className="text-xs text-[#F59E0B]">Nexus sigue trabajando: «Finalizar tarea» no está disponible hasta que termine.</p>}
      {(wrap.options ?? CLOSE_ACTIONS_UI.map((o) => ({ action: o.action, label: o.title }))).map((o) => {
        const disabled = isCloseActionDisabled(o.action, active.nexusRunning);
        const selected = choice === o.action;
        return (
          <label key={o.action} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 ${selected ? 'border-[#FF5E1E] bg-[#FF5E1E]/10' : 'border-outline-variant'} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}>
            <input type="radio" name="close-action" className="mt-1" checked={selected} disabled={disabled} onChange={() => onChoice(o.action)} data-testid={`close-action-${o.action}`} />
            <span>
              <span className="block text-sm font-medium text-on-surface">{o.label}</span>
              <span className="block text-xs text-on-surface-variant">{closeActionDesc(o.action)}</span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

export function FinishDayDialog({ wrap: initial, onClose, onChanged }: { wrap: WorkdayWrap; onClose: () => void; onChanged?: () => void | Promise<void> }) {
  const [wrap, setWrap] = useState(initial);
  const [choice, setChoice] = useState<CloseAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<CloseDayResult | null>(null);
  const needs = wrap.active.needsDecision;

  const confirm = async () => {
    if (busy || !canConfirmClose(needs, choice)) return;
    setBusy(true); setError(null);
    try {
      const r = await activityApi.closeDay(needs ? choice ?? undefined : undefined);
      setDone(r);
      notifyActivityChanged();
      await onChanged?.();
    } catch (e) {
      setError(errText(e));
      try { setWrap(await activityApi.wrap()); } catch { /* se queda el resumen anterior */ }
    }
    setBusy(false);
  };

  const exit = done ? exitTimeOf(done.timeline) : null;
  const finished = wrap.workday.state === 'FINISHED';
  return (
    <Modal title="Finalizar jornada" onClose={onClose} wide>
      <div className="space-y-5" data-testid="finish-day-dialog">
        {done ? (
          <div className="space-y-3" data-testid="finish-day-done">
            <p className="flex items-center gap-2 text-lg font-semibold text-[#10B981]"><Icon name="check_circle" />JORNADA FINALIZADA</p>
            <p className="text-sm text-on-surface-variant" data-testid="finish-day-exit">
              {exit && <>Hora de salida registrada: <b className="text-on-surface">{exit}</b>. </>}Dirección ve tu última tarea, su resultado y la siguiente prevista.
            </p>
            {(done.warnings?.length ?? 0) > 0 && (
              <ul className="list-disc pl-5 text-sm text-[#F59E0B]">{done.warnings!.map((w) => <li key={w}>{w}</li>)}</ul>
            )}
            <div className="flex justify-end"><button className={btnPrimary} onClick={onClose}>Cerrar</button></div>
          </div>
        ) : (
          <>
            <ErrorBanner message={error} onClose={() => setError(null)} />
            {finished && (
              <p className="rounded-lg border border-outline-variant px-3 py-2 text-sm text-on-surface-variant">
                Tu jornada de hoy ya está finalizada{wrap.workday.clockOut ? ` (salida ${fmtClockTime(wrap.workday.clockOut)})` : ''}.
              </p>
            )}
            <Summary wrap={wrap} />
            {needs && <Decision wrap={wrap} choice={choice} onChoice={setChoice} />}
            <div className="flex flex-wrap justify-end gap-2">
              <button className={btnGhost} onClick={onClose}>Cancelar</button>
              <button className={btnPrimary} disabled={busy || finished || !canConfirmClose(needs, choice)} onClick={confirm} data-testid="finish-day-confirm"><Icon name="logout" className="text-base" />FINALIZAR JORNADA</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export function FinishDayButton({ onChanged, className }: { onChanged?: () => void | Promise<void>; className?: string }) {
  const [wrap, setWrap] = useState<WorkdayWrap | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true); setFailure(null);
    try { setWrap(await activityApi.wrap()); setOpened(true); }
    catch (e) { setFailure(errText(e)); setOpened(true); }
    setBusy(false);
  };
  const close = () => { setOpened(false); setWrap(null); setFailure(null); };

  return (
    <>
      <button className={className ?? btnGhost} data-testid="finish-day" disabled={busy} onClick={open}><Icon name="logout" className="text-base" />FINALIZAR JORNADA</button>
      {opened && wrap && <FinishDayDialog wrap={wrap} onClose={close} onChanged={onChanged} />}
      {opened && !wrap && (
        <Modal title="Finalizar jornada" onClose={close} wide>
          <div className="space-y-5" data-testid="finish-day-dialog"><ErrorBanner message={failure} onClose={() => setFailure(null)} /></div>
        </Modal>
      )}
    </>
  );
}

export default FinishDayButton;
